import React, { createContext, useContext, useState, useEffect } from 'react';
import { CONTACT_PHONE_DISPLAY } from '../constants/contact';

interface LanguageContextType {
  language: string;
  setLanguage: (language: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language files
const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.treatment': 'Prominent Ear Treatment',
    'nav.about': 'About',
    'nav.beforeAfter': 'Before & After',
    'nav.testimonials': 'Testimonials',
    'nav.videos': 'Videos',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    
    // Hero Section
    'hero.badge': 'Expert Prominent Ear Treatment',
    'hero.title.line1': 'Professional',
    'hero.title.line2': 'Prominent Ear',
    'hero.title.line3': 'Treatment',
    'hero.subtitle': 'Doç. Dr. Ayşe Sezim provides world-class prominent ear treatment with modern surgical techniques and personalized care for lasting confidence.',
    'hero.form.title': 'Prominent Ear Treatment',
    'hero.form.subtitle': 'Start your journey to a more confident and secure life today',
    'hero.form.name': 'Full Name',
    'hero.form.countryCode': 'Country Code',
    'hero.form.phone': 'Phone Number',
    'hero.form.phonePlaceholder': '5xx xxx xx xx',
    'hero.form.email': 'Email Address',
    'hero.form.message': 'Your Message',
    'hero.form.messagePlaceholder': 'Please describe your prominent ear condition and concerns...',
    'hero.form.submit': 'Get Free Consultation',
    'hero.form.security': 'Your information is secure and will never be shared',
    
    // Treatment Info
    'treatment.title': 'Understanding Prominent Ears',
    'treatment.description': 'Prominent ears, also known as protruding ears, occur when the ear cartilage is more prominent and extends outward from the head. This condition can cause self-confidence issues, especially in children and adults.',
    'treatment.approach.title': 'Our Treatment Approach',
    'treatment.approach.description': 'We use modern otoplasty techniques to achieve natural and aesthetic results in prominent ear treatment. We prepare personalized treatment plans for each patient.',
    'treatment.approach.item1': 'Comprehensive ear analysis',
    'treatment.approach.item2': 'Personalized surgical plan',
    'treatment.approach.item3': 'Modern otoplasty techniques',
    'treatment.approach.item4': 'Long-term follow-up care',
    'treatment.expert.title': 'Professional Treatment',
    'treatment.expert.description': 'Accurate diagnosis and personalized surgical plans are essential for effective prominent ear treatment. Doç. Dr. Ayşe Sezim provides expert care with modern surgical techniques and experienced approach.',
    
    // About Section
    'about.title': 'About Doç. Dr. Ayşe Sezim',
    'about.description': 'Doç. Dr. Ayşe Sezim is a distinguished specialist in Otolaryngology and Aesthetic Surgery, with extensive knowledge and expertise in prominent ear treatment. She aims to provide the best service with modern and effective solutions, offering personalized treatment plans tailored to each individual\'s needs.',
    'about.credentials.title': 'Areas of Expertise',
    'about.credentials.item1': 'Prominent Ear Treatment (Otoplasty)',
    'about.credentials.item2': 'Ear, Nose and Throat Health',
    'about.credentials.item3': 'Aesthetic Surgery Applications',
    'about.credentials.item4': 'Modern Treatment Methods',
    
    // Treatments Section
    'treatments.title': 'Prominent Ear Treatment Services',
    'treatments.subtitle': 'Professional prominent ear surgery with international standards',
    'treatments.item1.title': 'Otoplasty Surgery',
    'treatments.item1.description': 'Prominent ear correction surgery with modern otoplasty techniques. Special surgical approaches for natural appearance and permanent results.',
    'treatments.item2.title': 'Prominent Ear Diagnosis & Treatment',
    'treatments.item2.description': 'Comprehensive ear analysis and personalized treatment plans. Suitable surgical and non-surgical options for all age groups.',
    
    // Testimonials
    'testimonials.title': 'Patient Testimonials',
    'testimonials.country': 'Istanbul, Turkey',
    'testimonials.subtitle': 'Real experiences from our prominent ear treatment patients',
    'testimonials.testimonial1.name': '',
    'testimonials.testimonial1.text': 'My 12-year-old son had prominent ear surgery. It was very successful and he is very satisfied. We thank Doç. Dr. Ayşe Sezim and her colleagues very much, we had a very healthy process. We recommend Doç. Dr. Ayşe Sezim to those considering prominent ear surgery.',
    'testimonials.testimonial2.name': '',
    'testimonials.testimonial2.text': 'We had our 6-year-old son\'s prominent ear surgery on January 17th. Doç. Dr. Ayşe Sezim\'s communication with children and surgical skills really satisfied us. Her assistant was also very caring. We were in constant contact with her assistant throughout the process. Today we also had our 1-month check-up. Thank God there are no problems. I recommend it to those considering it.',
    'testimonials.testimonial3.name': '',
    'testimonials.testimonial3.text': 'I had prominent ear surgery and the result is exactly what I wanted. It looks very natural. I experienced neither pain nor bruising. This problem that has bothered me since childhood has been solved, I am very happy.',
    'testimonials.testimonial4.name': '',
    'testimonials.testimonial4.text': 'I came to the doctor for prominent ear surgery, she is very caring, they show great interest after the surgery too, thank you for everything, I definitely recommend it.',
    'testimonials.testimonial5.name': '',
    'testimonials.testimonial5.text': 'I had prominent ear surgery one month ago. I had a trouble-free surgery process. Doç. Dr. Ayşe Sezim and her assistant Selen were very caring during the process. They inform you about the entire process, they are very experienced and successful in their work. I am very satisfied with the result. I recommend it to everyone.',
    'testimonials.testimonial6.name': '',
    'testimonials.testimonial6.text': 'I applied to Doç. Dr. Ayşe Sezim for single prominent ear surgery. I was worried at first whether it would be equal to my other ear, but Doç. Dr. Ayşe Sezim really broke all my prejudices. After a successful surgery, it became almost equal to my other ear. I wholeheartedly recommend her to those who want to go and will go. She is a competent doctor.',
    
    // Videos
    'videos.title': 'Assoc. Prof. Dr. Ayşe Sezim on YouTube',
    'videos.subtitle': 'Watch informative videos about prominent ear treatment and otoplasty surgery',
    'videos.watchAll': 'View All Videos',
    'videos.video1.title': 'What Is Prominent Ear? How Is It Treated?',
    'videos.video2.title': 'What Is Prominent Ear? How Is It Treated?',
    
    // Before/After
    'beforeAfter.title': 'Before & After Photos',
    'beforeAfter.subtitle': 'See the amazing transformations achieved through our professional prominent ear treatments',
    'beforeAfter.before': 'Before',
    'beforeAfter.after': 'After',
    'beforeAfter.case': 'Case',
    'beforeAfter.note': 'Results may vary from person to person. Individual results may differ.',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Common questions about prominent ear treatment',
    'faq.question1': 'What are prominent ears?',
    'faq.answer1': 'Prominent ears occur when the ear cartilage is more prominent and extends outward from the head. This is usually a congenital condition.',
    'faq.question2': 'How are prominent ears treated?',
    'faq.answer2': 'Prominent ear treatment is usually performed with otoplasty surgery. This surgery reshapes the ear cartilage to provide a natural appearance.',
    'faq.question3': 'At what age can prominent ear treatment be performed?',
    'faq.answer3': 'Prominent ear treatment can usually be performed from the age of 5-6. It can also be applied at any age in adults.',
    'faq.question4': 'How long does the surgery take?',
    'faq.answer4': 'Otoplasty surgery usually takes 1-2 hours. The patient can be discharged the same day.',
    'faq.question5': 'Is prominent ear treatment covered by insurance?',
    'faq.answer5': 'Since prominent ear treatment is an aesthetic procedure, it is usually not covered by insurance. However, partial coverage may be possible in some special cases.',
    
    // Contact
    'contact.title': 'Free Consultation',
    'contact.subtitle': 'Start your journey to a more confident and secure life today',
    'contact.form.name': 'Full Name',
    'contact.form.countryCode': 'Country Code',
    'contact.form.phone': 'Phone Number',
    'contact.form.phonePlaceholder': '5xx xxx xx xx',
    'contact.form.email': 'Email Address',
    'contact.form.submit': 'Get Free Consultation',
    'contact.security': 'Your information is secure and will never be shared',
    'form.countryCodePlaceholder': '🏳️',
    'form.phoneHint': 'Please enter your number without a leading 0',
    'form.phoneValidation': 'Phone number must be 8 to 12 digits.',
    'form.country.turkey': 'Turkey',
    'form.country.germany': 'Germany',
    'form.country.belgium': 'Belgium',
    'form.country.netherlands': 'Netherlands',
    'form.country.switzerland': 'Switzerland',
    'form.country.austria': 'Austria',
    'form.country.france': 'France',
    'form.country.poland': 'Poland',
    'form.country.denmark': 'Denmark',

    'form.success': 'Form submitted successfully! We will contact you soon.',
    'form.error': 'Error submitting form. Please try again.',
    
    // Thank You Page
    'thankyou.title': 'Thank You',
    'thankyou.subtitle': 'Your form has been submitted successfully.',
    'thankyou.description': 'Our team will contact you as soon as possible.',
    'thankyou.phone': CONTACT_PHONE_DISPLAY,
    'thankyou.email': 'info@draysesezim.com',
    'thankyou.goHome': 'Return to Home',
    'thankyou.whatsapp': 'Contact via WhatsApp',
    'thankyou.additionalInfo': 'Assoc. Prof. Dr. Ayşe Sezim Şafak',
    
    // Meta Tags
    'meta.title': 'Doç. Dr. Ayşe Sezim - Prominent Ear Treatment Services in Turkey | Expert Otoplasty',
    'meta.description': 'Doç. Dr. Ayşe Sezim - Expert Prominent Ear Treatment Services in Turkey. Professional otoplasty surgery with international standards. Modern prominent ear treatment and aesthetic surgery.',
    'meta.keywords': 'prominent ears, prominent ear treatment, otoplasty, ear aesthetics, turkey prominent ear, istanbul otoplasty, dr ayse sezim safak, ear surgery',
    'meta.ogTitle': 'Doç. Dr. Ayşe Sezim - Prominent Ear Treatment Services in Turkey',
    'meta.ogDescription': 'Expert Prominent Ear Treatment Services in Turkey. Professional otoplasty surgery with international standards.',
    
    // Footer
    'footer.services': 'Services',
    'footer.procedures': 'Procedures',
    'footer.contactInfo': 'Contact Information',
    'footer.location': 'Istanbul, Turkey',
    'footer.copyright': '© 2026 Doç. Dr. Ayşe Sezim Şafak. All rights reserved.',
    'footer.designedBy': 'Designed by',
    'footer.medicalDisclaimer': 'This website is for informational purposes only. It does not constitute medical advice. Always consult your doctor for treatment.',
    
    'cta.free_consultation': 'Free Consultation',
  },
  
  tr: {
    // Navigation
    'nav.home': 'Ana Sayfa',
    'nav.treatment': 'Kepçe Kulak Tedavisi',
    'nav.about': 'Hakkımızda',
    'nav.beforeAfter': 'Öncesi Sonrası',
    'nav.testimonials': 'Yorumlar',
    'nav.videos': 'Videolar',
    'nav.faq': 'SSS',
    'nav.contact': 'İletişim',
    
    // Hero Section
    'hero.badge': 'Uzman Kepçe Kulak Tedavisi',
    'hero.title.line1': 'Profesyonel',
    'hero.title.line2': 'Kepçe Kulak',
    'hero.title.line3': 'Tedavisi',
    'hero.subtitle': 'Doç. Dr. Ayşe Sezim, modern cerrahi teknikler ve kişiselleştirilmiş bakım ile dünya standartlarında kepçe kulak tedavisi sunmaktadır.',
    'hero.form.title': 'Kepçe Kulak Tedavisi',
    'hero.form.subtitle': 'Daha güvenli ve özgüvenli bir yaşama bugün başlayın',
    'hero.form.name': 'Ad Soyad',
    'hero.form.countryCode': 'Alan Kodu',
    'hero.form.phone': 'Telefon Numarası',
    'hero.form.phonePlaceholder': '5xx xxx xx xx',
    'hero.form.email': 'E-posta Adresi',
    'hero.form.message': 'Mesajınız',
    'hero.form.messagePlaceholder': 'Lütfen kepçe kulak durumunuzu ve endişelerinizi açıklayın...',
    'hero.form.submit': 'Ücretsiz Görüşme Al',
    'hero.form.security': 'Bilgileriniz güvenlidir ve asla paylaşılmaz',
    
    // Treatment Info
    'treatment.title': 'Kepçe Kulak Nedir?',
    'treatment.description': 'Kepçe kulak, kulak kepçesinin normalden daha belirgin ve dışa doğru çıkık görünmesi durumudur. Bu durum özellikle çocuklarda ve yetişkinlerde özgüven sorunlarına yol açabilir.',
    'treatment.approach.title': 'Tedavi Yaklaşımımız',
    'treatment.approach.description': 'Kepçe kulak tedavisinde modern otoplasti teknikleri kullanarak doğal ve estetik sonuçlar elde ediyoruz. Her hasta için kişiselleştirilmiş tedavi planı hazırlıyoruz.',
    'treatment.approach.item1': 'Kapsamlı kulak analizi',
    'treatment.approach.item2': 'Kişiselleştirilmiş cerrahi plan',
    'treatment.approach.item3': 'Modern otoplasti teknikleri',
    'treatment.approach.item4': 'Uzun vadeli takip bakımı',
    'treatment.expert.title': 'Profesyonel Tedavi',
    'treatment.expert.description': 'Etkili kepçe kulak tedavisi için doğru tanı ve kişiselleştirilmiş cerrahi planlar gereklidir. Doç. Dr. Ayşe Sezim, modern cerrahi teknikler ve deneyimli yaklaşımıyla uzman bakım sağlar.',
    
    // About Section
    'about.title': 'Doç. Dr. Ayşe Sezim Hakkında',
    'about.description': 'Doç. Dr. Ayşe Sezim, Kulak Burun Boğaz ve Estetik Cerrahi alanında uzman, kepçe kulak tedavisinde uzun yıllara dayanan bilgi birikimi ve geniş uzmanlık alanı ile sizlere en iyi hizmeti sunmayı hedeflemektedir. Modern ve etkili çözümlerle, her bireyin ihtiyaçlarına özel olarak hazırladığı tedavi planları ile kişiye özel bir yaklaşım sergiler.',
    'about.credentials.title': 'Uzmanlık Alanları',
    'about.credentials.item1': 'Kepçe Kulak Tedavisi (Otoplasti)',
    'about.credentials.item2': 'Kulak Burun Boğaz Sağlığı',
    'about.credentials.item3': 'Estetik Cerrahi Uygulamaları',
    'about.credentials.item4': 'Modern Tedavi Yöntemleri',
    
    // Treatments Section
    'treatments.title': 'Kepçe Kulak Tedavi Hizmetleri',
    'treatments.subtitle': 'Uluslararası standartlarda profesyonel kepçe kulak cerrahisi',
    'treatments.item1.title': 'Otoplasti Cerrahisi',
    'treatments.item1.description': 'Modern otoplasti teknikleri ile kepçe kulak düzeltme ameliyatı. Doğal görünüm ve kalıcı sonuçlar için özel cerrahi yaklaşımlar.',
    'treatments.item2.title': 'Kepçe Kulak Tanı ve Tedavisi',
    'treatments.item2.description': 'Kapsamlı kulak analizi ve kişiselleştirilmiş tedavi planları. Her yaş grubu için uygun cerrahi ve cerrahi dışı seçenekler.',
    
    // Testimonials
    'testimonials.title': 'Hasta Yorumları',
    'testimonials.country': 'İstanbul, Türkiye',
    'testimonials.subtitle': 'Kepçe kulak tedavi hastalarımızdan gerçek deneyimler',
    'testimonials.testimonial1.name': '',
    'testimonials.testimonial1.text': '12 yaşında oğlum kepçe kulak ameliyat oldu. Çok başarılı geçti kendisi de çok memnun. Doç. Dr. Ayşe Sezim ve çalışma arkadaşlarına çok teşekkür ederiz çok sağlıklı bir süreç geçirdik. Kepçe kulak ameliyatı düşünenler varsa biz Doç. Dr. Ayşe Sezim\'i tavsiye ederiz.',
    'testimonials.testimonial2.name': '',
    'testimonials.testimonial2.text': '17 ocakta 6yaşındaki oğlumuzun kepçe kulak ameliyatını yaptırdık. Doç. Dr. Ayşe Sezim\'in hem çocukla iletişimi hem de cerrahlığı gerçekten bizi çok memnun etti. Asistanı da çok ilgili. Süreçte asistanı ile de sürekli irtibat halindeydik. Bugün 1 ay kontrolünü de yaptırdık. Çok şükür bir sorun yok. Düşünenlere tavsiye ediyorum.',
    'testimonials.testimonial3.name': '',
    'testimonials.testimonial3.text': 'Kepçe kulak ameliyatı oldum ve sonuç tam istediğim gibi. Çok doğal duruyor. Ne ağrı ne de morarma yaşadım. Çocukluğumdan beri rahatsız olduğum bu sorun çözüldü, çok mutluyum.',
    'testimonials.testimonial4.name': '',
    'testimonials.testimonial4.text': 'Doktor hanıma kepçe kulak ameliyatı için gelmiştim, kendisi çok ilgililer, ameliyattan sonra da çok ilgi gösteriyorlar her şey için teşekkür ederim kesinlikle tavsiye ederim.',
    'testimonials.testimonial5.name': '',
    'testimonials.testimonial5.text': 'Bir ay önce kepçe kulak ameliyatı oldum. Sorunsuz bir ameliyat süreci geçirdim. Doktorum Doç. Dr. Ayşe Sezim ve asistanı Selen hanım süreçte çok ilgilendiler. Tüm süreç hakkında sizi bilgilendiriyorlar, işlerinde çok tecrübeli ve başarılılar. Sonuçtan çok memnunum. Herkese tavsiye ederim.',
    'testimonials.testimonial6.name': '',
    'testimonials.testimonial6.text': 'Tek kepçe kulak operasyonu için Doç. Dr. Ayşe Sezim\'e başvurdum başta tedirgindim diğer kulağımla eşit olurmu diye ama gerçekten Doç. Dr. Ayşe Sezim tüm ön yargılarımı bozdu başarılı bir ameliyat sonrası diğer kulağımla neredeyse eşit oldu gitmek isteyen ve gidecek olan kişilere canı gönülden tavsiye ederim işinin ehli bir doktor.',
    
    // Videos
    'videos.title': 'YouTube\'da Doç. Dr. Ayşe Sezim',
    'videos.subtitle': 'Kepçe kulak tedavisi ve otoplasti cerrahisi hakkında bilgilendirici videoları izleyin',
    'videos.watchAll': 'Tüm Videoları Görüntüle',
    'videos.video1.title': 'Kepçe Kulak Estetiği Nedir?',
    'videos.video2.title': 'Kepçe Kulak Estetiği Nedir?',
    
    // Before/After
    'beforeAfter.title': 'Öncesi ve Sonrası Fotoğraflar',
    'beforeAfter.subtitle': 'Profesyonel kepçe kulak tedavilerimizle elde edilen harika dönüşümleri görün',
    'beforeAfter.before': 'Öncesi',
    'beforeAfter.after': 'Sonrası',
    'beforeAfter.case': 'Vaka',
    'beforeAfter.note': 'Sonuçlar kişiden kişiye değişebilir. Bireysel sonuçlar farklılık gösterebilir.',
    
    // FAQ
    'faq.title': 'Sık Sorulan Sorular',
    'faq.subtitle': 'Kepçe kulak tedavisi hakkında yaygın sorular',
    'faq.question1': 'Kepçe kulak nedir?',
    'faq.answer1': 'Kepçe kulak, kulak kepçesinin normalden daha belirgin ve dışa doğru çıkık görünmesi durumudur. Genellikle doğuştan gelen bir durumdur.',
    'faq.question2': 'Kepçe kulak nasıl tedavi edilir?',
    'faq.answer2': 'Kepçe kulak tedavisi genellikle otoplasti cerrahisi ile yapılır. Bu ameliyat kulak kıkırdağını yeniden şekillendirerek doğal görünüm sağlar.',
    'faq.question3': 'Hangi yaşta kepçe kulak tedavisi yapılabilir?',
    'faq.answer3': 'Kepçe kulak tedavisi genellikle 5-6 yaşından itibaren yapılabilir. Yetişkinlerde de her yaşta uygulanabilir.',
    'faq.question4': 'Ameliyat ne kadar sürer?',
    'faq.answer4': 'Otoplasti ameliyatı genellikle 1-2 saat sürer. Hasta aynı gün taburcu edilebilir.',
    'faq.question5': 'Kepçe kulak tedavisi sigorta tarafından karşılanıyor mu?',
    'faq.answer5': 'Kepçe kulak tedavisi estetik bir işlem olduğu için genellikle sigorta kapsamında değildir. Ancak bazı özel durumlarda kısmi karşılama mümkün olabilir.',
    
    // Contact
    'contact.title': 'Ücretsiz Görüşme',
    'contact.subtitle': 'Daha güvenli ve özgüvenli bir yaşama bugün başlayın',
    'contact.form.name': 'Ad Soyad',
    'contact.form.countryCode': 'Alan Kodu',
    'contact.form.phone': 'Telefon Numarası',
    'contact.form.phonePlaceholder': '5xx xxx xx xx',
    'contact.form.email': 'E-posta Adresi',
    'contact.form.submit': 'Ücretsiz Görüşme Al',
    'contact.security': 'Bilgileriniz güvenlidir ve asla paylaşılmaz',
    'form.countryCodePlaceholder': '🏳️',
    'form.phoneHint': 'Lütfen numaranızı başında 0 olmadan giriniz',
    'form.phoneValidation': 'Telefon numarası 8 ile 12 hane arasında olmalıdır.',
    'form.country.turkey': 'Türkiye',
    'form.country.germany': 'Almanya',
    'form.country.belgium': 'Belçika',
    'form.country.netherlands': 'Hollanda',
    'form.country.switzerland': 'İsviçre',
    'form.country.austria': 'Avusturya',
    'form.country.france': 'Fransa',
    'form.country.poland': 'Polonya',
    'form.country.denmark': 'Danimarka',
    
    'form.success': 'Form başarıyla gönderildi! En kısa sürede sizinle iletişime geçeceğiz.',
    'form.error': 'Form gönderilirken hata oluştu. Lütfen tekrar deneyin.',
    
    // Thank You Page
    'thankyou.title': 'Teşekkürler',
    'thankyou.subtitle': 'Formunuz başarıyla gönderildi.',
    'thankyou.description': 'Ekibimiz en kısa sürede sizinle iletişime geçecektir.',
    'thankyou.phone': CONTACT_PHONE_DISPLAY,
    'thankyou.email': 'info@draysesezim.com',
    'thankyou.goHome': 'Ana Sayfaya Dön',
    'thankyou.whatsapp': 'WhatsApp ile İletişime Geç',
    'thankyou.additionalInfo': 'Doç. Dr. Ayşe Sezim Şafak',
    
    // Meta Tags
    'meta.title': 'Doç. Dr. Ayşe Sezim - Türkiye\'de Kepçe Kulak Tedavi Hizmetleri | Uzman Otoplasti',
    'meta.description': 'Doç. Dr. Ayşe Sezim - Türkiye\'de Uzman Kepçe Kulak Tedavi Hizmetleri. Uluslararası standartlarda profesyonel otoplasti cerrahisi. Modern kepçe kulak tedavisi ve estetik cerrahi.',
    'meta.keywords': 'kepçe kulak, kepçe kulak tedavisi, otoplasti, kulak estetiği, türkiye kepçe kulak, istanbul otoplasti, dr ayşe sezim şafak, kulak cerrahisi',
    'meta.ogTitle': 'Doç. Dr. Ayşe Sezim - Türkiye\'de Kepçe Kulak Tedavi Hizmetleri',
    'meta.ogDescription': 'Türkiye\'de Uzman Kepçe Kulak Tedavi Hizmetleri. Uluslararası standartlarda profesyonel otoplasti cerrahisi.',
    
    // Footer
    'footer.services': 'Hizmetler',
    'footer.procedures': 'Prosedürler',
    'footer.contactInfo': 'İletişim Bilgileri',
    'footer.location': 'İstanbul, Türkiye',
    'footer.copyright': '© 2026 Doç. Dr. Ayşe Sezim Şafak. Tüm hakları saklıdır.',
    'footer.designedBy': 'Designed by',
    'footer.medicalDisclaimer': 'Bu web sitesi sadece bilgilendirme amaçlıdır. Tıbbi tavsiye niteliği taşımaz. Tedavi için mutlaka doktorunuza danışın.',
    
    'cta.free_consultation': 'Ücretsiz Görüşme',
  }
};

const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<string>('tr'); // Default to Turkish

  useEffect(() => {
    // Load language from localStorage or default to Turkish
    const savedLanguage = localStorage.getItem('language') || 'tr';
    setLanguage(savedLanguage);
  }, []);

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key: string): string => {
    const langTranslations = translations[language as keyof typeof translations];
    if (langTranslations && key in langTranslations) {
      return langTranslations[key as keyof typeof langTranslations];
    }
    return key;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: handleLanguageChange, 
      t 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { LanguageProvider, useLanguage };
